import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { api, errMsg, fmtDate } from "@/lib/api";

export const useTripComms = (tripId, intervalMs = 3000) => {
  const [data, setData] = useState({ messages: [], calls: [], read_only: true });
  const load = useCallback(async () => {
    try { setData((await api.get(`/trips/${tripId}/messages`)).data); } catch { /* keep last */ }
  }, [tripId]);
  useEffect(() => { load(); const t = setInterval(load, intervalMs); return () => clearInterval(t); }, [load, intervalMs]);
  return [data, load];
};

const ROLE_STYLE = { admin: "border-amber-400/40 bg-amber-400/10", customer: "bg-white/[0.04]", driver: "bg-white/[0.04]" };

export const MessageList = ({ messages, myRole }) => {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [messages.length]);
  if (!messages.length) return <p data-testid="chat-empty" className="py-6 text-center text-sm text-zinc-500">No messages yet.</p>;
  return (
    <div className="space-y-3" data-testid="chat-messages">
      {messages.map((m) => {
        const mine = m.sender_role === myRole;
        return (
          <div key={m.id} data-testid="chat-message" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl border border-white/5 px-4 py-2.5 ${mine ? "bg-amber-400/15" : ROLE_STYLE[m.sender_role]}`}>
              <p data-testid="chat-message-sender" className="text-[11px] font-semibold text-amber-300">{m.sender_name}</p>
              <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-zinc-100">{m.text}</p>
              <p className="mt-1 text-[10px] text-zinc-500">{fmtDate(m.created_at)}</p>
            </div>
          </div>
        );
      })}
      <div ref={end} />
    </div>
  );
};

export const MessageComposer = ({ tripId, onSent, testPrefix = "chat" }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/trips/${tripId}/messages`, { text });
      setText("");
      onSent();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={send} className="flex gap-2">
      <input data-testid={`${testPrefix}-input`} className="lux-input min-w-0 flex-1" placeholder="Type a message…" value={text} maxLength={2000} onChange={(e) => setText(e.target.value)} />
      <button type="submit" data-testid={`${testPrefix}-send-button`} disabled={!text.trim() || busy} className="btn-gold !px-4" aria-label="Send">
        <Send className="h-4 w-4" />
      </button>
    </form>
  );
};

export const ChatButton = ({ tripId, myRole, title }) => {
  const [{ messages, read_only }, reload] = useTripComms(tripId);
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button data-testid="chat-open-button" className="relative rounded-full border border-amber-500/30 p-2.5 text-amber-300 transition-colors hover:bg-amber-400/10" aria-label="Messages">
          <MessageSquare className="h-4 w-4" />
          {messages.length > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-bold text-black">{messages.length}</span>}
        </button>
      </SheetTrigger>
      <SheetContent side="right" data-testid="chat-sheet" className="flex w-full flex-col border-amber-500/20 bg-[#0e0e12] p-0 sm:max-w-md">
        <SheetHeader className="border-b border-white/5 px-5 py-4 text-left"><SheetTitle className="font-serif text-amber-50">{title}</SheetTitle></SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 py-4"><MessageList messages={messages} myRole={myRole} /></div>
        <div className="border-t border-white/5 p-4">
          {read_only ? <p className="text-center text-xs text-zinc-500">Conversation is read-only.</p> : <MessageComposer tripId={tripId} onSent={reload} />}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const CallButton = ({ tripId, phone, label }) => {
  const log = () => { api.post(`/trips/${tripId}/calls`).catch(() => {}); };
  if (!phone) return null;
  return (
    <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} onClick={log} data-testid="call-button" aria-label={label}
      className="rounded-full border border-amber-500/30 p-2.5 text-amber-300 transition-colors hover:bg-amber-400/10">
      <Phone className="h-4 w-4" />
    </a>
  );
};
